<template>
  <div class="services">
    <header class="head">
      <input class="search" v-model="search" :placeholder="$t('randevu.services.search_placeholder')" />
      <button class="primary" @click="openModal('ServiceAdd')">
        <img class="icon" src="../icons/plusw.svg" /> {{ $t('randevu.services.new') }}
      </button>
    </header>

    <div v-if="loading" class="info">{{ $t('common.loading') }}</div>
    <div v-if="error" class="error">{{ error }}</div>

    <div v-if="filtered.length" class="card-list">
      <div class="service-card" v-for="s in filtered" :key="s.id">
        <div class="card-body">
          <h3>{{ s.name }}</h3>
          <p><strong>{{ $t('randevu.services.duration_label') }}</strong> {{ s.duration || '-' }} {{ $t('randevu.services.duration_unit') }}</p>
          <p><strong>{{ $t('randevu.services.price_label') }}</strong> {{ s.price || '-' }} {{ currency(s.currency_id) }}</p>
          <p class="desc"><strong>{{ $t('randevu.services.description_label') }}</strong> {{ s.description || $t('randevu.services.no_description') }}</p>
        </div>
        <div class="card-actions">
          <button class="btn-icon" @click="openModal('ServiceAdd', s)">
            <img src="../icons/duzenle.svg" :alt="$t('randevu.services.edit_alt')" />
          </button>
          <button class="btn-icon" @click="confirmDelete(s.id)">
            <img src="../icons/sil.svg" :alt="$t('randevu.services.delete_alt')" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="!filtered.length && !loading && !error" class="info">{{ $t('randevu.services.empty') }}</div>

    <div v-if="showModal" class="modal-overlay" @click="overlay">
      <div ref="modal" class="modal-content" :style="style" @click.stop>
        <div class="close-div">
          <button class="close-button" @click="close">X</button>
        </div>
        <component
          :is="current"
          :service="selected"
          :serviceId="selectedId"
          @refresh="fetch"
          @close="close"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import ServiceAdd from "./ServiceAdd.vue";
import ServiceDetail from "./ServiceDetail.vue";

export default {
  name: "MyServices",
  components: { ServiceAdd, ServiceDetail },
  setup() {
    const { t } = useI18n();
    const services = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const search = ref("");

    const filtered = computed(() =>
      services.value.filter(s =>
        s.name.toLowerCase().includes(search.value.toLowerCase())
      )
    );

    const showModal = ref(false);
    const current = ref(null);
    const selected = ref(null);
    const selectedId = ref(null);
    const style = ref({ transform: "translateY(100%)", transition: "transform .3s ease" });

    const currency = id =>
      id === 1 ? "₺" : id === 2 ? "$" : id === 3 ? "€" : "";

    const fetch = async () => {
      loading.value = true;
      error.value = null;
      try {
        const { data } = await axios.get("/promo/user/my-services");
        services.value = data;
      } catch (err) {
        if (err.response && err.response.status === 404) {
          error.value = t("randevu.services.empty_404");
          services.value = [];
        } else {
          error.value = t("randevu.services.fetch_error");
        }
      } finally {
        loading.value = false;
      }
    };

    const openModal = (comp, service = null) => {
      current.value = comp;
      selected.value = typeof service === "object" ? service : null;
      selectedId.value = typeof service === "number" ? service : null;
      showModal.value = true;
      nextTick(() =>
        setTimeout(() => (style.value.transform = "translateY(0)"), 10)
      );
    };

    const close = () => {
      style.value.transform = "translateY(100%)";
      setTimeout(() => {
        showModal.value = false;
        current.value = null;
      }, 400);
    };

    const overlay = e => {
      if (e.target === e.currentTarget) close();
    };

    const confirmDelete = async id => {
      if (!confirm(t("randevu.services.delete_confirm"))) return;
      try {
        await axios.delete(`/promo/user/my-services/${id}`);
        await fetch();
      } catch {
        error.value = t("randevu.services.delete_error");
      }
    };

    onMounted(fetch);

    return {
      services,
      loading,
      error,
      search,
      filtered,
      currency,
      openModal,
      showModal,
      current,
      selected,
      selectedId,
      style,
      close,
      overlay,
      confirmDelete,
      fetch
    };
  }
};
</script>

<style scoped>
.head {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 1em;
}
.search {
  flex: 1 1 180px;
  padding: 8px;
}
.primary {
  background: linear-gradient(45deg, #106e35, #106e35,  #003615); 
  border: solid 1px #106e35;
  cursor: pointer;
  border-radius: 0;
  transition: all 0.3s ease; 
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  color: white;
}
.primary:hover {
  border-color: #1d964d;
  color: #e4e4e4;
  transform: scale(1.05); 
}
.icon {
  max-width: 1rem;
}

.card-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1em;
}

.service-card {
  background: linear-gradient(145deg, #050505, #0e0e0e);
  border: 1px solid #1a1a1a;
  border-radius: 4px;
  padding: 1em;
  color: #ddd;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

h3 {
  font-size: 1.3em;
  font-weight: 300;
  margin: 3px;
}
p {
  margin: 3px;
}
.card-body {
  flex: 1;
}
.card-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 12px;
}
.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
}
.btn-icon img {
  width: 20px;
  height: 20px;
  filter: brightness(1.2); 
  transition: all 0.2s ease; 
}
.btn-icon:hover img {  transform: scale(1.1);
  filter: brightness(1.5);
} 
.info {
  margin-top: 12px;
  color: #aaa;
}
.error {
  font-weight: 500;
  margin-top: 12px;
  color: #917e5c;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
}
.modal-content {
  margin-top: 12rem;
  width: 100%;
  max-width: 1080px;
  background: linear-gradient(45deg, #020202, #080808, #030b0b);
  border-radius: 10px 10px 0 0;
  border-top: 1px solid #022121;
  border-left: 1px solid #022121;
  border-right: 1px solid #022121;
  padding: 1rem;
  overflow-y: auto;
  max-height: 80vh;
}
.close-div {
  position: sticky;
  border-radius: 15px 15px 0 0;
  top: 0;
  display: flex;
  justify-content: flex-end;
  z-index: 1;
  padding: 0.5rem;
}

.close-button {
  background-color: #000000;
  color: #ffffff;
  padding: 0.8rem;
  min-width: 3rem;
  max-width: 3rem;
  border-radius: 2rem;
  opacity: 0.8;
  border: 1px solid #444444;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.close-button:hover {
  opacity: 1;
  transform: scale(1.1);
}
</style>
